import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import LTVAnalytics from './LTVAnalytics';
import './Analytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const Analytics = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const years = [2024, 2025];

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedYear]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/analytics?year=${selectedYear}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const data = await response.json();
      setAnalyticsData(data.data);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="error-state">
          <h2>⚠️ Error Loading Analytics</h2>
          <p>{error}</p>
          <button onClick={fetchAnalyticsData} className="retry-btn">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="analytics-container">
        <div className="no-data-state">
          <h2>📊 No Analytics Data Available</h2>
          <p>No data found for the selected period.</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const monthlyRevenueData = {
    labels: analyticsData.monthlyData.map(item => item.month),
    datasets: [
      {
        label: 'Revenue',
        data: analyticsData.monthlyData.map(item => item.revenue),
        borderColor: '#2D2F8E',
        backgroundColor: 'rgba(45, 47, 142, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const monthlyProfitData = {
    labels: analyticsData.monthlyData.map(item => item.month),
    datasets: [
      {
        label: 'Profit',
        data: analyticsData.monthlyData.map(item => item.profit),
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const lessonsByLabelsData = {
    labels: analyticsData.lessonsByLabels?.map(label => label.label) || [],
    datasets: [
      {
        data: analyticsData.lessonsByLabels?.map(label => label.count) || [],
        backgroundColor: [
          '#10B981', '#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6',
          '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const tutorPerformanceData = {
    labels: analyticsData.topTutors?.map(tutor => tutor.name) || [],
    datasets: [
      {
        label: 'Revenue Generated',
        data: analyticsData.topTutors?.map(tutor => tutor.revenue) || [],
        backgroundColor: 'rgba(45, 47, 142, 0.8)',
        borderColor: '#2D2F8E',
        borderWidth: 1
      }
    ]
  };

  const monthlyMetricsData = {
    labels: analyticsData.monthlyData.map(item => item.month),
    datasets: [
      {
        label: 'Lessons',
        data: analyticsData.monthlyData.map(item => item.lessons),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3B82F6',
        borderWidth: 1
      },
      {
        label: 'Revenue',
        data: analyticsData.monthlyData.map(item => item.revenue / 100), // Scale down for better visualization
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: '#10B981',
        borderWidth: 1
      }
    ]
  };

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-content">
          <h1>📊 Analytics Dashboard</h1>
          <p>Historical performance data and insights</p>
        </div>
        <div className="year-selector">
          <label htmlFor="year-select">Year:</label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="year-dropdown"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'ltv' ? 'active' : ''}`}
          onClick={() => setActiveTab('ltv')}
        >
          💎 Lifetime Value
        </button>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Key Metrics Cards */}
          <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">💰</div>
          <div className="metric-content">
            <h3>Total Revenue</h3>
            <p className="metric-value">{formatCurrency(analyticsData.financialSummary.totalRevenue)}</p>
            <p className="metric-change positive">+{analyticsData.financialSummary.revenueGrowth}% vs previous year</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📈</div>
          <div className="metric-content">
            <h3>Total Profit</h3>
            <p className="metric-value">{formatCurrency(analyticsData.financialSummary.totalProfit)}</p>
            <p className="metric-change positive">+{analyticsData.financialSummary.profitGrowth}% vs previous year</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">🎯</div>
          <div className="metric-content">
            <h3>Profit Margin</h3>
            <p className="metric-value">{analyticsData.financialSummary.profitMargin}%</p>
            <p className="metric-change positive">+{analyticsData.financialSummary.marginGrowth}% vs previous year</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📚</div>
          <div className="metric-content">
            <h3>Total Lessons</h3>
            <p className="metric-value">{formatNumber(analyticsData.lessonsSummary.total)}</p>
            <p className="metric-change positive">+{analyticsData.lessonsSummary.growth}% vs previous year</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Revenue Trend */}
        <div className="chart-container">
          <h3>📈 Monthly Revenue Trend</h3>
          <Line 
            data={monthlyRevenueData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value) {
                      return formatCurrency(value);
                    }
                  }
                }
              }
            }}
          />
        </div>

        {/* Profit Trend */}
        <div className="chart-container">
          <h3>💵 Monthly Profit Trend</h3>
          <Line 
            data={monthlyProfitData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value) {
                      return formatCurrency(value);
                    }
                  }
                }
              }
            }}
          />
        </div>

        {/* Lessons by Labels */}
        <div className="chart-container">
          <h3>📊 Lessons by Labels</h3>
          <Doughnut 
            data={lessonsByLabelsData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
                title: {
                  display: false,
                },
              }
            }}
          />
        </div>

        {/* Top Performing Tutors */}
        <div className="chart-container">
          <h3>🏆 Top Performing Tutors</h3>
          <Bar 
            data={tutorPerformanceData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const tutorData = analyticsData.topTutors?.[context.dataIndex];
                      if (!tutorData) return context.formattedValue;
                      return [
                        `Revenue: ${formatCurrency(tutorData.revenue)}`,
                        `Lessons: ${tutorData.lessons}`,
                        `Avg per lesson: ${formatCurrency(tutorData.avgRevenuePerLesson)}`
                      ];
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value) {
                      return formatCurrency(value);
                    }
                  }
                }
              }
            }}
          />
        </div>

        {/* Monthly Metrics Comparison */}
        <div className="chart-container full-width">
          <h3>📊 Monthly Metrics Overview</h3>
          <Bar 
            data={monthlyMetricsData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value, index) {
                      if (index === 0) return value; // Lessons
                      return formatCurrency(value * 100); // Revenue (scale back up)
                    }
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="detailed-stats">
        <div className="stats-section">
          <h3>📋 Detailed Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Average Revenue per Lesson:</span>
              <span className="stat-value">{formatCurrency(analyticsData.performanceMetrics.avgRevenuePerLesson)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Average Tutor Pay per Lesson:</span>
              <span className="stat-value">{formatCurrency(analyticsData.performanceMetrics.avgTutorPayPerLesson)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Average Profit per Lesson:</span>
              <span className="stat-value">{formatCurrency(analyticsData.performanceMetrics.avgProfitPerLesson)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Ad Hoc Pay:</span>
              <span className="stat-value">{formatCurrency(analyticsData.adHocPaySummary.totalAdHocPay)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Completion Rate:</span>
              <span className="stat-value">{analyticsData.lessonsSummary.completionRate}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Duration (Completed):</span>
              <span className="stat-value">{analyticsData.lessonsSummary.totalDuration}</span>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* LTV Analytics Tab */}
      {activeTab === 'ltv' && <LTVAnalytics />}
    </div>
  );
};

export default Analytics;
